import { computed } from 'vue';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

import { useGetCheckAuthApi } from '@/api/auth';
import {
	ALREADY_LOGGED_IN,
	CANCEL,
	COMING_SOON,
	COMPANY_TITLE,
	CONFIRM,
	CONFIRM_LOGIN,
	FEATURE_UNDER_DEVELOPMENT,
	LOGIN_REQUIRED_PAGE,
	MESSAGE_PC_MENU_UNDER_PREPARATION,
} from '@/constants/title';
import router from '@/router';
import { useHeaderStore } from '@/stores/header';
import { useLoginPopupStore } from '@/stores/LoginPopup';
import { useModalStore } from '@/stores/modal';
import { usePopupStore } from '@/stores/popup';
import { useTokenStore } from '@/stores/token';

import { checkId } from './convertValue';

// 로그인 router 상태 체크
export const checkLogin = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const tokenStore = useTokenStore();
	const loginPopupStore = useLoginPopupStore();
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		next(false);
	} else {
		next();
	}
};

export const checkLoginAuto = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	// * Auto: 즉시 라우팅 처리
	const tokenStore = useTokenStore();
	if (!tokenStore.isLogin) {
		next(false);
		router.push('/auth/login');
	} else {
		next();
	}
};

export const redirectPaymentLink = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const tokenStore = useTokenStore();
	const loginPopupStore = useLoginPopupStore();
	if (tokenStore.isLogin) {
		next('/temp-payment');
		return;
	}
	loginPopupStore.openPopup({
		icon: 'alert',
		content: LOGIN_REQUIRED_PAGE,
		confirm: () =>
			router.push({
				name: 'auth/login',
				query: { redirect: '/temp-payment' },
			}),
		confirmText: CONFIRM_LOGIN,
		cancelText: CANCEL,
	});
	next(false);
};

export const checkID = async (_: RouteLocationNormalized, __: RouteLocationNormalized, next: NavigationGuardNext) => {
	const popupStore = usePopupStore();
	if (!checkId()) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: MESSAGE_PC_MENU_UNDER_PREPARATION,
		});
		next(false);
	} else {
		next();
	}
};

export const checkIDForJPN = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const headerStore = useHeaderStore();
	const popupStore = usePopupStore();

	// JPN 국가일 때만 checkID 로직 실행
	if (headerStore.getCountry === 'JPN' && !checkId()) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: MESSAGE_PC_MENU_UNDER_PREPARATION,
		});
		next(false);
	} else {
		next();
	}
};

export const setJpnCountry = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const headerStore = useHeaderStore();
	if (headerStore.getCountry !== 'JPN') {
		headerStore.setCountry('JPN');
	}
	next();
};
export const setUsaCountry = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const headerStore = useHeaderStore();
	if (headerStore.getCountry !== 'USA') {
		headerStore.setCountry('USA');
	}
	next();
};
export const setKrCountry = async (
	_: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const headerStore = useHeaderStore();
	if (headerStore.getCountry !== 'KR') {
		headerStore.setCountry('KR');
	}
	next();
};

export const setCountry = async (
	_to: RouteLocationNormalized,
	__: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const headerStore = useHeaderStore();

	const country = _to.params.country as string;
	headerStore.setCountry(country.toUpperCase() as 'KR' | 'USA' | 'JPN');
	next();
};

// 로그인 컴포넌트 상태 체크
export const checkLoginDetail = async () => {
	const tokenStore = useTokenStore();
	const loginPopupStore = useLoginPopupStore();
	if (!tokenStore.isLogin) {
		loginPopupStore.openPopup({
			icon: 'alert',
			content: LOGIN_REQUIRED_PAGE,
			confirm: () => router.push('/auth/login'),
			confirmText: CONFIRM_LOGIN,
			cancelText: CANCEL,
		});
		return;
	}
};
// 7인 이상 구독자 컴포넌트 상태 체크
export const checkSubScribeAllDetail = async () => {
	const { refetch: checkAuthRefetch } = useGetCheckAuthApi(
		computed(() => {
			return {
				num: 'ALL',
				student: false,
			};
		})
	);
	const modalStore = useModalStore();
	const res = await checkAuthRefetch();
	if (!res.data?.success) {
		modalStore.openModal('GuideModal');
		return;
	}
};
// 로그아웃 상태 체크
export const checkLogout = async (
	_: RouteLocationNormalized,
	_from: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const tokenStore = useTokenStore();
	const loginPopupStore = usePopupStore();
	if (tokenStore.isLogin) {
		loginPopupStore.openPopup({
			content: ALREADY_LOGGED_IN,
			confirm: () => router.replace('/'),
			confirmText: CONFIRM,
		});
		next(false);
	} else {
		next();
	}
};

// 7인 구독 유무
export const checkSubscribeAll = async (
	_: RouteLocationNormalized,
	_from: RouteLocationNormalized,
	next: NavigationGuardNext
) => {
	const { refetch: checkAuthRefetch } = useGetCheckAuthApi(
		computed(() => {
			return {
				num: 'ALL',
				student: false,
			};
		})
	);
	const modalStore = useModalStore();
	const res = await checkAuthRefetch();
	if (!res.data?.success) {
		modalStore.openModal('GuideModal');
		next(false);
	} else {
		next();
	}
};
// 개발중
export const devBlocker = (_: RouteLocationNormalized, __: RouteLocationNormalized, next: NavigationGuardNext) => {
	const loginPopupStore = usePopupStore();
	loginPopupStore.openPopup({
		content: FEATURE_UNDER_DEVELOPMENT,
		subtext: COMING_SOON,
		isRouteConsist: true,
		showTime: 3000,
	});
	next(false);
};
